/**
 * LoggerService - Non-blocking logger that sends logs to backend via HTTP POST
 *
 * Features:
 * - Queue-based non-blocking log dispatch
 * - Batch sending for better performance
 * - Buffer logs when offline
 * - Multiple log levels (debug, info, warn, error)
 * - Console fallback when backend is unavailable
 */

import { cleanCircularReferences } from "../tools/CircularReferenceTools";

export enum LogLevel {
    "DEBUG" = "debug",
    "INFO" = "info",
    "WARN" = "warn",
    "ERROR" = "error",
}

interface LogMessage {
    "level":       LogLevel;
    "message":     string;
    "timestamp":   string;
    "context"?:    string;
    "data"?:       unknown;
    "source"?:     string;
    "clientId"?:   string;
    "retryCount"?: number;
}

class LoggerServiceClass {

    private logQueue: LogMessage[] = [];

    private readonly maxQueueSize: number = 1000;

    private readonly batchSize: number = 10;

    private readonly flushInterval: number = 100; // 100ms pour max 10 req/sec

    private flushTimer: NodeJS.Timeout | null = null;

    private isSending: boolean = false;

    private debugMode: boolean = false;

    private apiUrl!: string;

    private clientId!: string;

    private nextRetryAtMs: number = 0;

    private lastNetworkWarningAtMs: number = 0;

    private readonly networkWarningThrottleMs: number = 30000;

    private readonly networkBackoffMs: number = 5000;

    // Store original console methods to avoid circular dependency with ConsoleInterceptor
    private originalConsole!: {
        "log":   typeof console.log;
        "debug": typeof console.debug;
        "info":  typeof console.info;
        "warn":  typeof console.warn;
        "error": typeof console.error;
    };

    constructor() {
        // Determine API URL based on environment
        this.apiUrl = "/api/logs"; // Use proxy configuration from vite.config.ts

        // Generate unique client ID
        this.clientId = this.generateClientId();

        // Initialize original console methods BEFORE any logging operations
        this.originalConsole = {
            "log":   console.log.bind( console ),
            "debug": console.debug.bind( console ),
            "info":  console.info.bind( console ),
            "warn":  console.warn.bind( console ),
            "error": console.error.bind( console ),
        };

        this.checkDebugMode();


        this.startFlushTimer();
    }

    /**
     * Generate unique client ID
     */
    private generateClientId(): string {
        return `client-${ Date.now() }-${ Math.random().toString( 36 ).
            substr( 2, 9 ) }`;
    }

    /**
     * Safe logging method that cannot be intercepted
     * Uses the original console methods stored before any patching
     */
    private safeLog( ...args: unknown[] ): void {
        this.originalConsole.log( ...args );
    }

    /**
     * Detect network-level fetch errors in a browser-compatible way
     */
    private isNetworkError( error: unknown ): boolean {
        if ( !( error instanceof TypeError ) ) {
            return false;
        }

        const message = error.message.toLowerCase();

        return message.includes( "failed to fetch" ) || message.includes( "network" ) || message.includes( "load failed" );
    }

    /**
     * Log network outage warning with throttling to prevent console spam
     */
    private logNetworkWarning( immediateError: boolean ): void {
        const now = Date.now();
        if ( now - this.lastNetworkWarningAtMs < this.networkWarningThrottleMs ) {
            return;
        }

        this.lastNetworkWarningAtMs = now;

        if ( immediateError ) {
            this.safeLog( "[LoggerService] ⚠️ Network error: Unable to send error log immediately" );
            this.safeLog( "[LoggerService] ⚠️ Error log will be queued for retry" );

            return;
        }

        this.safeLog( "[LoggerService] ⚠️ Network error: Unable to connect to logging backend" );
        this.safeLog( "[LoggerService] ⚠️ Logs will be queued for retry when connection is restored" );
    }

    /**
     * Safe JSON serializer that handles circular references and DOM elements
     */
    private safeJsonStringify( obj: unknown ): string {
        try {
            // Utiliser cleanCircularReferences au lieu de la logique inline
            const cleaned = cleanCircularReferences( obj, new WeakSet(), 0, 3 );

            return JSON.stringify( cleaned, null, 2 );
        } catch( error ) {
            return `[Stringify Error: ${ error }]`;
        }
    }

    /**
     * Check if debug mode is enabled from environment or localStorage
     */
    private checkDebugMode(): void {
        const isDevelopment = __IS_DEV__;

        this.debugMode = isDevelopment;
    }

    /**
     * Enable or disable debug mode
     */
    public setDebugMode( enabled: boolean ): void {
        this.debugMode = enabled;
        localStorage.setItem( "debug_logs", enabled.toString() );

        if ( enabled ) {
            // Flush any pending logs when enabling debug mode
            void this.flushQueue();
        }
    }

    /**
     * Get current debug mode status
     */
    public isDebugModeEnabled(): boolean {
        return this.debugMode;
    }

    /**
     * Check if the logging backend is reachable
     */
    public async checkBackendConnectivity(): Promise<boolean> {
        try {
            const response = await fetch( `${ this.apiUrl }/health`, { "method": "GET" } );

            return response.ok;
        } catch {
            return false;
        }
    }

    /**
     * Start periodic flush timer
     */
    private startFlushTimer(): void {
        this.flushTimer = setInterval( () => {
            void this.flushQueue();
        }, this.flushInterval );
    }

    /**
     * Add log to queue
     */
    private enqueueLog( log: LogMessage ): void {
        // Prevent queue overflow
        if ( this.logQueue.length >= this.maxQueueSize ) {
            this.logQueue.shift(); // Remove oldest log
        }

        // Add client ID to log
        log.clientId = this.clientId;

        this.logQueue.push( log );


        // Flush immediately if queue is getting large
        if ( this.logQueue.length >= this.batchSize ) {
            void this.flushQueue();
        }
    }

    /**
     * Send all queued logs to backend
     */
    private async flushQueue(): Promise<void> {
        if ( this.isSending || this.logQueue.length === 0 ) {
            return;
        }

        if ( Date.now() < this.nextRetryAtMs ) {
            return;
        }

        this.isSending = true;

        try {
            // Take batch from queue
            const batch = this.logQueue.splice( 0, this.batchSize );

            if ( batch.length === 0 ) {
                return;
            }


            // Send logs to backend with proper error handling
            try {
                const response = await fetch( `${ this.apiUrl }/batch`, {
                    "method":  "POST",
                    "headers": { "Content-Type": "application/json" },
                    "body":    this.safeJsonStringify( { "logs": batch } ),
                } );

                if ( !response.ok ) {
                    const errorText = await response.text();
                    this.safeLog( `[LoggerService] ❌ Failed to send logs to backend: ${ response.status } ${ response.statusText }` );
                    this.safeLog( "[LoggerService] ❌ Error response:", errorText );
                }
            } catch( error ) {
                // Handle network errors gracefully
                if ( this.isNetworkError( error ) ) {
                    this.logNetworkWarning( false );
                    this.nextRetryAtMs = Date.now() + this.networkBackoffMs;
                } else {
                    this.safeLog( "[LoggerService] ❌ Unexpected error sending logs:", error );
                }

                // Re-queue the batch for retry (but limit retries to prevent infinite loops)
                if ( batch.length > 0 ) {
                    // Only re-queue if we haven't exceeded max retries
                    const retryCount = batch[0]?.retryCount || 0;
                    if ( retryCount < 3 ) {
                        // Add retry count to logs
                        const retryBatch = batch.map( ( log ) => ( {
                            ...log,
                            "retryCount": retryCount + 1,
                        } ) );

                        // Add back to front of queue for retry
                        this.logQueue.unshift( ...retryBatch );

                        this.safeLog( `[LoggerService] 🔄 Re-queued ${ batch.length } logs for retry (attempt ${ retryCount + 1 }/3)` );
                    } else {
                        this.safeLog( `[LoggerService] ❌ Dropped ${ batch.length } logs after 3 failed attempts` );
                    }
                }
            }
        } finally {
            this.isSending = false;
        }
    }

    /**
     * Send single log immediately (used for errors)
     */
    private async sendLogImmediately( log: LogMessage ): Promise<void> {
        try {
            log.clientId = this.clientId;

            // Clean circular references before sending
            const cleanedLog = {
                ...log,
                "data": cleanCircularReferences( log.data ),
            };

            await fetch( this.apiUrl, {
                "method":  "POST",
                "headers": { "Content-Type": "application/json" },
                "body":    this.safeJsonStringify( cleanedLog ),
            } );
        } catch( error ) {
            // Handle network errors gracefully for immediate sends
            if ( this.isNetworkError( error ) ) {
                this.logNetworkWarning( true );
                this.nextRetryAtMs = Date.now() + this.networkBackoffMs;

                // Queue the error log for retry instead of losing it
                this.enqueueLog( log );
            } else {
                this.safeLog( "[LoggerService] ❌ Unexpected error sending immediate log:", error );
            }
        }
    }

    /**
     * Create a log message
     */
    private createLogMessage(
        level: LogLevel,
        message: string,
        context?: string,
        data?: unknown,
    ): LogMessage {
        // Get caller info from stack trace
        const stack = new Error().stack;
        let source = "unknown";

        if ( stack !== undefined ) {
            const stackLines = stack.split( "\n" );

            // Find the first line that's not from LoggerService
            for ( let i = 0; i < stackLines.length; i++ ) {
                const line = stackLines[i];
                if ( line !== undefined && !line.includes( "LoggerService" ) ) {
                    // Extract file and line number
                    const match = line.match( /\/([^/]+:\d+:\d+)/ );
                    if ( match !== null && match[1] !== undefined ) {
                        source = match[1];
                        break;
                    }
                }
            }
        }

        return {
            level,
            message,
            "timestamp": new Date().toISOString(),
            context,
            data,
            source,
        };
    }

    /**
     * Log debug message (called by ConsoleInterceptor)
     */
    public debug( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.DEBUG, message, context, data );

        /*
         * Always send to backend - ConsoleInterceptor calls this for all console.log/debug
         * Don't log to console here - ConsoleInterceptor already handled it
         */
        this.enqueueLog( logMessage );
    }

    /**
     * Log debug message directly (respects debug mode)
     */
    public debugDirect( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.DEBUG, message, context, data );

        // Always log to console using original method to avoid circular dependency
        this.originalConsole.log( `[${ context || "DEBUG" }]`, message, data );

        // Send to backend only if in debug mode
        if ( this.debugMode ) {
            this.enqueueLog( logMessage );
        }
    }

    /**
     * Log info message (called by ConsoleInterceptor)
     */
    public info( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.INFO, message, context, data );

        /*
         * Always send to backend - ConsoleInterceptor calls this for all console.info
         * Don't log to console here - ConsoleInterceptor already handled it
         */
        this.enqueueLog( logMessage );
    }

    /**
     * Log info message directly (respects debug mode)
     */
    public infoDirect( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.INFO, message, context, data );

        this.originalConsole.info( `[${ context || "INFO" }]`, message, data );

        // Send to backend only if in debug mode
        if ( this.debugMode ) {
            this.enqueueLog( logMessage );
        }
    }

    /**
     * Log warning message (called by ConsoleInterceptor)
     */
    public warn( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.WARN, message, context, data );

        /*
         * Always send to backend - ConsoleInterceptor calls this for all console.warn
         * Don't log to console here - ConsoleInterceptor already handled it
         */
        this.enqueueLog( logMessage );
    }

    /**
     * Log warning message directly (respects debug mode)
     */
    public warnDirect( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.WARN, message, context, data );

        this.originalConsole.warn( `[${ context || "WARN" }]`, message, data );

        // Send to backend only if in debug mode
        if ( this.debugMode ) {
            this.enqueueLog( logMessage );
        }
    }

    /**
     * Log error message
     */
    public error( message: string, context?: string, data?: unknown ): void {
        const logMessage = this.createLogMessage( LogLevel.ERROR, message, context, data );

        /*
         * Don't log to console here - ConsoleInterceptor already handled it
         * Always send errors to backend, even if debug mode is off
         */
        void this.sendLogImmediately( logMessage );
    }

    /**
     * Clear log queue
     */
    public clearQueue(): void {
        this.logQueue = [];
    }

    /**
     * Get queue size
     */
    public getQueueSize(): number {
        return this.logQueue.length;
    }

    /**
     * Manually flush queue
     */
    public async flush(): Promise<void> {
        await this.flushQueue();
    }

    /**
     * Cleanup on service destruction
     */
    public destroy(): void {
        if ( this.flushTimer !== null ) {
            clearInterval( this.flushTimer );
            this.flushTimer = null;
        }
        void this.flushQueue();
    }

}

// Export singleton instance
export const LoggerService = new LoggerServiceClass();
